import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog } from 'extended-oxygen-elements';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from 'oxygen-elements';
import { Collapse, List } from 'extended-oxygen-elements';
import { ListItemButton } from 'oxygen-elements';
import {
  ExpandLessOutlinedIcon,
  ExpandMoreOutlinedIcon,
  WarningAmberOutlinedIcon,
} from 'element';
import { getDutyIcon } from 'lib/helpers/dutyIcons';
import { UnmetDate } from 'lib/Api/duties';
import marketLabels from 'marketLabels';
import moment from 'moment';
import './MissingDutiesWarningModal.scss';

type Props = {
  open: boolean;
  unmetDates: UnmetDate[];
  onCancel: () => void;
  onIgnore: () => void;
};

const dutiesSettingsPath = `/app/settings/${marketLabels.roster}-settings/duties`;

const formatDateWithCount = (dateStr: string, missingCount: number): string =>
  `${moment(dateStr).format('dddd Do MMMM YYYY')} (${missingCount})`;

export const MissingDutiesWarningModal = ({
  open,
  unmetDates,
  onCancel,
  onIgnore,
}: Props) => {
  const [expandedDates, setExpandedDates] = useState<Record<string, boolean>>(
    {}
  );

  const toggleDate = (date: string) => {
    setExpandedDates((prev) => ({ ...prev, [date]: !prev[date] }));
  };

  return (
    <Dialog
      maxWidth="sm"
      open={open}
      onClose={onCancel}
      id="missing-duties-warning-modal"
    >
      <DialogTitle>
        <span className="missing-duties-modal__title">
          <WarningAmberOutlinedIcon className="missing-duties-modal__title-icon" />
          Missing duties in your {marketLabels.roster}
        </span>
      </DialogTitle>

      <DialogContent>
        <Typography
          variant="body2"
          className="missing-duties-modal__description"
          component="span"
        >
          There are missing duty assignments in your {marketLabels.roster} based on your{' '}
          <Link
            to={dutiesSettingsPath}
            className="missing-duties-modal__link"
            onClick={onCancel}
          >
            duty settings
          </Link>
          .
        </Typography>

        <List component="div" className="missing-duties-modal__list">
          {unmetDates.map((unmetDate) => {
            const isExpanded = expandedDates[unmetDate.date] ?? false;

            return (
              <div key={unmetDate.date} className="missing-duties-modal__date-group">
                <ListItemButton
                  onClick={() => toggleDate(unmetDate.date)}
                  className="missing-duties-modal__date-header"
                >
                  <div className="missing-duties-modal__date-info">
                    <Typography variant="body2" className="missing-duties-modal__date-text">
                      {formatDateWithCount(unmetDate.date, unmetDate.missingCount)}
                    </Typography>
                  </div>
                  {isExpanded ? (
                    <ExpandLessOutlinedIcon />
                  ) : (
                    <ExpandMoreOutlinedIcon />
                  )}
                </ListItemButton>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <div className="missing-duties-modal__duties">
                    {unmetDate.duties.map((duty) => (
                      <div
                        key={duty.dutyId}
                        className="missing-duties-modal__duty-badge"
                      >
                        {getDutyIcon(duty.key, 16)}
                        <span className="missing-duties-modal__duty-name">
                          {duty.name}
                        </span>
                        <span className="missing-duties-modal__duty-count">
                          {duty.assigned}/{duty.required}
                        </span>
                      </div>
                    ))}
                  </div>
                </Collapse>
              </div>
            );
          })}
        </List>
      </DialogContent>

      <DialogActions>
        <Button
          variant="text"
          onClick={onCancel}
          id="missing-duties-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onIgnore}
          id="missing-duties-ignore-btn"
        >
          Ignore
        </Button>
      </DialogActions>
    </Dialog>
  );
};
